import {useAuth} from '../AuthProvider/AuthProvider';
import {useJobContext} from './JobContextProvider';
import {isAllClientsScope} from '../../component/widgets/MediaMetrics/mediaMetricsDataRetrieving';

/**
 * What a header-scoped dashboard widget should ask ArkBridge for, given the state of the job selector.
 *
 * Three answers, and the reason this is a hook rather than four copies of the same three lines: the
 * dashboard's cards must agree about what 'All Jobs' means. They did not. Media Metrics aggregates over
 * every job the selector lists, while the report cards beside it sent no scope at all and got back every
 * company in `backupsaas_settings` — so two cards on one screen answered the same question differently,
 * and the reports were the ones adding up wrong (see below).
 *
 * - Jobs ticked in the header → those job ids, whether they sit under one client or several. A JobId
 *   identifies one job of one company, so a selection spanning clients is still one query, with no cid.
 * - Nothing ticked, admin → every job id the selector lists (`allJobIds`), because that is what the word
 *   'All' beside it refers to. Sending nothing instead is not the same request: it counts companies Nexus
 *   was never told about and media that was never onboarded as an order.
 * - Nothing ticked, customer user → no job ids and their own cid, exactly as before. Their token already
 *   bounds the query to their company, and their orders may carry no ArkBridge job id at all; substituting
 *   an empty list there would turn a working card into an empty one.
 *
 * The `xag_*` report tables give the aggregate substitution a second, sharper reason. A row with
 * `JobID = 0` in those tables means 'all jobs for this company' (AggregationTypes.py), so it holds the same
 * media as the per-job rows beside it. An unscoped report query returns both and sums them, double-counting
 * every company that has a roll-up row and per-job rows — locally that is company 161, whose roll-up and
 * whose job 239 carry an identical 158,004 files. Naming the job ids excludes the roll-up.
 *
 * @returns {{jobIds: number[], cid: number|undefined, jobIdsKey: string, waiting: boolean,
 *           nothingOnboarded: boolean, allClientsScope: boolean}}
 *   `jobIds`/`cid` go to the fetch; `jobIdsKey` is the stable useEffect dependency (`allJobIds` is sorted
 *   and deduplicated by the provider, so this string only changes when the scope really does); `waiting`
 *   means do not fetch yet; `nothingOnboarded` means there is nothing to fetch at all.
 */
export const useDashboardJobScope = () => {
    /*
     * Defaulted rather than assumed. Pages and test harnesses mock this context with the selection fields
     * only, and a missing job list has to degrade to 'no aggregate to take' rather than to a TypeError on
     * the dashboard — the same reason MediaMetrics defaults them.
     */
    const {selectedJobIds = [], selectedCid, allJobIds = [], jobGroupsPending = false} = useJobContext();
    const {isAdmin} = useAuth();

    const allClientsScope = isAllClientsScope(isAdmin, selectedCid);
    const substituting = allClientsScope && selectedJobIds.length === 0;
    const jobIds = substituting ? allJobIds : selectedJobIds;

    /*
     * The header fetches the job list, so a widget substituting it has to wait for it.
     *
     * Without the wait the widget fires unscoped on mount and again scoped a moment later: two heavy
     * report queries per card for one page view, and the figures the reader sees are whichever answered
     * first — which for the report cards is precisely the double-counted ones. Not waited for when jobs are
     * ticked: those ids are already the scope, so a list still in flight cannot change the answer.
     */
    const waiting = substituting && jobGroupsPending;
    /*
     * Nothing onboarded: reported as no data, not by dropping the scope.
     *
     * Dropping it would fall back to the platform-wide figures this scope exists to stop showing, on a
     * portal whose Jobs list is empty. 'No Data' is the answer that matches the selector beside it.
     */
    const nothingOnboarded = substituting && !jobGroupsPending && jobIds.length === 0;

    return {jobIds, cid: selectedCid, jobIdsKey: jobIds.join(','), waiting, nothingOnboarded, allClientsScope};
};
